/**
 * iter-156sf — defensive guard against orphaned Radix scroll-locks.
 *
 * Radix (via react-remove-scroll) locks page scroll while a Dialog /
 * Select / Popover is open by setting `overflow: hidden` +
 * `pointer-events: none` on <body> and a `data-scroll-locked` attribute.
 * Normally it cleans these up on close — but Safari's bfcache can
 * restore a page (back/forward navigation) with those styles frozen in
 * place, leaving the whole page unscrollable. A component that unmounts
 * before its cleanup effect runs can leave the same orphan.
 *
 * This hook clears any leftover lock on every route change AND on a
 * bfcache restore (`pageshow` with `persisted`) — but ONLY when no Radix
 * overlay is actually open, so it never fights a legitimate lock.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function anyOverlayOpen() {
  return !!document.querySelector(
    '[data-radix-dialog-content][data-state="open"],' +
    '[role="dialog"][data-state="open"],' +
    '[data-radix-popper-content-wrapper],' +
    '[data-radix-select-content][data-state="open"]',
  );
}

function clearOrphanScrollLock() {
  if (anyOverlayOpen()) return;
  const b = document.body;
  if (
    b.hasAttribute('data-scroll-locked') ||
    b.style.overflow === 'hidden' ||
    b.style.pointerEvents === 'none'
  ) {
    b.removeAttribute('data-scroll-locked');
    b.style.removeProperty('overflow');
    b.style.removeProperty('pointer-events');
    if (!b.getAttribute('style')) b.removeAttribute('style');
  }
}

export function useScrollLockGuard() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Defer past the current render so any closing overlay finishes its
    // own teardown first; we only mop up what it left behind.
    const t = setTimeout(clearOrphanScrollLock, 0);
    return () => clearTimeout(t);
  }, [pathname]);

  useEffect(() => {
    const onPageShow = (e) => { if (e.persisted) clearOrphanScrollLock(); };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);
}
